/**
 * @module components/TabsTableHazard
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Tab display of various hazard table displays
 */

import { useMemo, useState } from 'react';

import { Row, Col, Tabs, Tab, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';

import { flattenSiteDesignations } from '../lib';

import { useLanguageState } from '../contexts/language-state-provider';

import ChartAmp from './chart/chart-amp';
import ChartHazard from './chart/chart-hazard';
import ChartUHS from './chart/chart-uhs';


type Props = {
  variables: HazardParameters
  data: SiteDesignationsXVResponse | SiteDesignationsXSResponse,
}


/**
 * React component
 * ===============
 */
const TabsChartHazard = ({
  variables,
  data,
}: Props) => {
  const [ lang ] = useLanguageState();
  const [ selectedProbability, setSelectedProbability ] = useState<number>(2);

  // Get all probabilties to plot in the tab
  const probabilties = useMemo(() => {
    const probabilties = new Set<number>()
    data.userSpecified.forEach(v => {
      probabilties.add(v.poe50);
    })
    return [...probabilties].sort((a,b)=>a-b);
  }, [data]);

  // flatten the data (list instead of object)
  const flatData = useMemo(() => flattenSiteDesignations(data), [data]);

  // flatten userSpecified only (list instead of object)
  const flatUserData = useMemo(() => flattenSiteDesignations(
    //@ts-ignore: we know this works
    {'userSpecified': data['userSpecified']},
  ), [data]);

  return (
    <>
      {lang.tab.plotsDetail}
      <Tabs defaultActiveKey="uhs" id="tabChartHazard" unmountOnExit={true}>
        <Tab eventKey="uhs" title={lang.chart.uhs}>
          <ChartUHS
            variables={variables}
            siteDesignations={flatUserData}
          />
        </Tab>
        <Tab eventKey="hazardCurve" title={lang.chart.hazardCurve}>
          <ChartHazard
            variables={variables}
            siteDesignations={flatUserData}
          />
        </Tab>
        <Tab eventKey="siteAmplification" title={lang.chart.siteAmplification}>
          <Row>
            <FormGroup controlId="chartProb">
              <Col componentClass={ControlLabel} sm={2}>
                {lang.chart.probabilitySelect}
              </Col>
              <Col sm={10}>
                <FormControl
                  componentClass='select'
                  value={selectedProbability}
                  onChange={e => {
                    setSelectedProbability(Number((e.target as HTMLButtonElement).value))
                  }}
                >
                  {probabilties.map((option) => (
                    <option key={option} value={option}>
                      {lang.chart.siteAmplification} - {option}%/50y
                    </option>
                  ))}
                </FormControl>
              </Col>
            </FormGroup>
          </Row>
          <br/>
          <ChartAmp
            variables={variables}
            probability={Number(selectedProbability)}
            vs30={data.userSpecified[0].vs30}
            siteDesignations={flatData}
          />
        </Tab>
      </Tabs>
    </>
  );
};

export default TabsChartHazard;